import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ClipboardCheck, Plus } from "lucide-react";
import { inspectionsApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { fmtDate, statusTone, prettyStatus } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { LoadingState, ErrorState, EmptyState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const FILTERS = ["all", "pass", "fail", "review", "no_access"];

export default function Inspections() {
  const navigate = useNavigate();
  const { data, loading, error, reload } = useAsync(() => inspectionsApi.getAll({ source: "all" }), []);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const list = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return list.filter((i) => {
      if (filter !== "all" && String(i.status).toLowerCase() !== filter) return false;
      if (!s) return true;
      return (
        (i.building_name || "").toLowerCase().includes(s) ||
        (i.door_reference || "").toLowerCase().includes(s) ||
        (i.inspector_name || "").toLowerCase().includes(s)
      );
    });
  }, [list, q, filter]);

  return (
    <div>
      <PageHeader title="Inspections" subtitle="Every fire-door inspection across your buildings" actions={<Button onClick={() => navigate("/inspections/new")} data-testid="new-inspection-btn"><Plus className="mr-1.5 h-4 w-4" /> New inspection</Button>} testId="inspections-header" />

      {loading && <LoadingState label="Loading inspections…" />}
      {!loading && error && <ErrorState message={error} onRetry={reload} />}

      {!loading && !error && (
        <>
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative max-w-sm flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Search building, door or inspector…" className="pl-10" value={q} onChange={(e) => setQ(e.target.value)} data-testid="inspections-search" />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {FILTERS.map((f) => (
                <Button
                  key={f}
                  size="sm"
                  variant={filter === f ? "default" : "outline"}
                  onClick={() => setFilter(f)}
                  data-testid={`inspection-filter-${f}`}
                >
                  {f === "all" ? "All" : prettyStatus(f)}
                </Button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <EmptyState
              title={list.length === 0 ? "No inspections yet" : "No matches"}
              message={list.length === 0 ? "Inspections recorded in the field will appear here." : "Try a different search or filter."}
              testId="inspections-empty"
            />
          ) : (
            <div className="space-y-2" data-testid="inspections-list">
              {filtered.map((i) => (
                <Card
                  key={i.id}
                  className="flex cursor-pointer items-center justify-between gap-3 p-4 transition-shadow hover:shadow-md"
                  onClick={() => navigate(`/inspections/${i.id}`)}
                  data-testid={`inspection-row-${i.id}`}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                      <ClipboardCheck className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate font-medium text-slate-800">
                        {i.door_reference || "Door"} · <span className="font-normal text-slate-500">{i.building_name || "Building"}</span>
                      </p>
                      <p className="truncate text-sm text-slate-500">{fmtDate(i.inspection_date)} · {i.inspector_name || "—"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {i.has_quote && <Badge variant="outline" className="hidden border-blue-200 bg-blue-50 text-blue-700 sm:inline-flex">Quoted</Badge>}
                    <Badge variant="outline" className={statusTone(i.status)}>{prettyStatus(i.status)}</Badge>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
